import { useMemo } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useGetScalingSize } from '@ui/Report';
import {
  buildLeaderLinePathD,
  buildProductGroupLeaderLines,
} from './product-groups-donut-leader-lines.helpers';
import { PRODUCT_GROUPS_LEADER_LINE_COLOR } from './report-product-groups.typography';
import { PRODUCT_GROUPS_LEGEND_LINE_THICKNESS } from './report-product-groups.constants';
import type { ProductGroupSegment } from './report-product-groups.model';

type ProductGroupsDonutLeaderLinesProps = {
  segments: ProductGroupSegment[];
  rotationOffsetDeg?: number;
};

/**
 * Leader lines from each donut slice to the legend — radial stub, circular track
 * arc outside the donut, then horizontal into the row.
 */
export const ProductGroupsDonutLeaderLines: React.FC<
  ProductGroupsDonutLeaderLinesProps
> = ({ segments, rotationOffsetDeg = 0 }) => {
  const getScalingSize = useGetScalingSize();
  const { classes } = useStyles({ getScalingSize });
  const lines = useMemo(
    () => buildProductGroupLeaderLines(segments, { rotationOffsetDeg }),
    [segments, rotationOffsetDeg],
  );

  if (lines.length === 0) return null;

  return (
    <Box className={classes.root}>
      <svg
        className={classes.svg}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {lines.map((line) => (
          <path
            key={line.id}
            className={classes.line}
            d={buildLeaderLinePathD(line.points)}
            fill="none"
            stroke={PRODUCT_GROUPS_LEADER_LINE_COLOR}
            strokeOpacity={0.6}
            vectorEffect="non-scaling-stroke"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
      </svg>
    </Box>
  );
};

const useStyles = makeStyles<{
  getScalingSize: ReturnType<typeof useGetScalingSize>;
}>()((_theme, { getScalingSize }) => ({
  root: {
    position: 'absolute',
    inset: 0,
    overflow: 'visible',
    pointerEvents: 'none',
    opacity: 1,
    zIndex: 1,
  },
  svg: {
    display: 'block',
    width: '100%',
    height: '100%',
    overflow: 'visible',
    opacity: 1,
  },
  line: {
    strokeWidth: getScalingSize(PRODUCT_GROUPS_LEGEND_LINE_THICKNESS),
  },
}));
